import React, {
	useState,
	PropsWithChildren,
	useEffect,
	useContext,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IControlProps, IFormMeta, FormStatus } from '../types/IFormSettings'
import {
	controlSelector,
	formKeySelector,
} from '../selectors/formBuilder.selector'
import FormTooltip from './FormTooltip'
import { InputTypes } from '../types/InputTypes'
import { isAntiFraudControl } from '../helpers/antiFraudHelper'
import { hasTF21Theme } from '../helpers'
import { markets } from '../constants'
import {
	updateControlState,
	updateFormControlValueAction,
} from '../actions/formActions'
import { FieldLockModeContext } from '../context/FieldLockModeContext'
import { FormLockModeContext } from '../context/FormLockModeContext'

const hasLongLabel = (label: string) =>
	label.normalize().length > 31 ? ' isLong' : ''

const valueClassHandler = (value: any, cn: string[], type: InputTypes) => {
	let classNames = cn
	if (
		value === '' ||
		(value === false && ['checkbox', 'switch'].indexOf(type) > -1)
	) {
		classNames = classNames.filter((className) => className !== 'has-value')

		return classNames.some((className) => className === 'is-empty')
			? classNames
			: classNames.concat(['is-empty'])
	}

	classNames = classNames.filter((className) => className !== 'is-empty')
	return classNames.some((className) => className === 'has-value')
		? classNames
		: classNames.concat(['has-value'])
}

function FormControlWrapper({
	controlName,
	formName,
	children,
}: PropsWithChildren<IControlProps>) {
	const {
		value,
		isValid,
		type,
		validationMessage,
		description,
		required,
		disabled,
		visible,
		label = '',
		tooltip,
		wrapperClass = '',
		col = 12,
		displayFixedLabel = false,
		options = [],
		inputPrefix,
	} = useSelector(controlSelector(formName, controlName))

	const { formMeta = {} as IFormMeta } =
		useSelector(formKeySelector(formName, 'initialSettings')) || {}
	const dispatch = useDispatch()
	const market = formMeta?.market
	const isTF21 = hasTF21Theme(market, formMeta?.visualTheme)
	const isMultiStep = formMeta?.isMultiStep

	const fieldLockMode = useContext(FieldLockModeContext)
	const formLockContext = useContext(FormLockModeContext)
	const [isLocked, setIsLocked] = useState(true)
	const formStatus = useSelector(formKeySelector(formName, 'formStatus'))

	// When a validation run marks this control invalid, auto-enter edit mode
	useEffect(() => {
		if (
			fieldLockMode &&
			formStatus === FormStatus.notValid &&
			isValid === false
		) {
			setIsLocked(false)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formStatus])
	const [localSuffix, setLocalSuffix] = useState<string | null>(null)

	const [baseClasses, setClasses] = useState([
		'field',
		'has-feedback',
		'form-group',
		`control-${type}`,
		value === null || value === undefined || value === ''
			? 'is-empty'
			: 'has-value', // 0 and false are VALID options in our dropdowns.
	])

	useEffect(() => {
		if (visible) return
		if (value !== '') {
			dispatch(
				updateControlState({
					formName,
					controlName: controlName,
					value: '',
				}),
			)
		}
	}, [controlName, dispatch, formName, value, visible])

	useEffect(() => {
		if (!fieldLockMode || isLocked || !inputPrefix) return
		if (!String(value ?? '').startsWith(inputPrefix)) {
			dispatch(
				updateControlState({
					formName,
					controlName,
					value: inputPrefix,
				}),
			)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLocked])

	if (visible === false) return <></>

	const onFocusHandler = () => {
		if (type === 'slider' || type === 'radio') return

		if (!baseClasses.some((className) => className === 'is-focused')) {
			setClasses(baseClasses.concat(['is-focused']))
		}
	}

	const onBlurHandler = ({
		relatedTarget,
		currentTarget,
	}: React.FocusEvent<HTMLInputElement>) => {
		if (type === 'radio' || type === 'slider') return
		let current = relatedTarget as HTMLElement

		// to not lose the focus inside the element
		while (current?.parentElement) {
			if (current.parentElement === currentTarget) {
				return
			}
			current = current.parentElement
		}

		let newClasses: string[] = baseClasses.filter(
			(className) => className !== 'is-focused',
		)

		newClasses = valueClassHandler(currentTarget.value, newClasses, type)

		setClasses(newClasses)
	}

	const classNamesArr = [...baseClasses]

	if (isValid === false) {
		classNamesArr.push('has-error')
	}

	if (disabled) {
		classNamesArr.push('disabled')
	}

	if (required) {
		classNamesArr.push('required')
	}

	if (market) {
		classNamesArr.push(market.toLowerCase())
	}

	if (
		['chips', 'dropdown', 'switch', 'datePicker', 'phoneNumber'].indexOf(
			type,
		) > -1 ||
		displayFixedLabel
	) {
		classNamesArr.push('label-fixed')
	} else if (type === 'radio') {
		classNamesArr.push('radio-label')
	} else {
		classNamesArr.push('label-floating')
	}

	if (isAntiFraudControl(formMeta.enableCrestt, controlName)) {
		classNamesArr.push('d-none')
	}

	const classNameString = valueClassHandler(value, classNamesArr, type).join(
		' ',
	)

	const validSpan = isValid === false &&
		['checkbox', 'radio', 'switch'].indexOf(type) === -1 && (
			<span error-for={controlName} className="error">
				{validationMessage}
			</span>
		)

	const tooltipJsx = tooltip && <FormTooltip tooltip={tooltip} />

	const labelJsx = ['tiles', 'chips', 'checkbox', 'documentUpload'].indexOf(
		type,
	) === -1 && (
		<label
			className={`control-label${hasLongLabel(label)}`}
			id={`anc_${controlName}`}
			htmlFor={controlName}>
			<span dangerouslySetInnerHTML={{ __html: label }} />
			{required ? <span className="required">*</span> : ''}
			{((market !== markets.germany && !isTF21) || isMultiStep) &&
			type !== 'checkbox'
				? tooltipJsx
				: ''}
		</label>
	)

	const descriptionJsx = (
		<div className="description col-12">
			{(market === markets.germany || isTF21) &&
				!isMultiStep &&
				tooltipJsx}
			<div dangerouslySetInnerHTML={{ __html: description || '' }} />
		</div>
	)

	if (fieldLockMode || (formLockContext.lockMode && formLockContext.locked)) {
		const getDisplayValue = (): string => {
			if (value === null || value === undefined || value === '')
				return '—'
			if (type === 'dropdown' && options.length) {
				if (String(value).includes(',')) {
					return String(value)
						.split(',')
						.map(
							(v) =>
								options.find(
									(o) => String(o.value) === v.trim(),
								)?.label ?? v.trim(),
						)
						.join(', ')
				}
				return (
					options.find((o) => String(o.value) === String(value))
						?.label ?? String(value)
				)
			}
			return String(value)
		}

		if (isLocked || (formLockContext.lockMode && formLockContext.locked)) {
			return (
				<div className={`form-box col-${col} ${wrapperClass}`}>
					<div className="field-lock-row">
						<div className="field-lock-info">
							<span className="field-lock-label">
								{label}
								{required ? (
									<span className="required">*</span>
								) : (
									''
								)}
							</span>
							<span className="field-lock-value">
								{getDisplayValue()}
							</span>
						</div>
						{!formLockContext.lockMode && (
							<input
								type="checkbox"
								className="field-lock-toggle"
								checked={true}
								onChange={() => setIsLocked(false)}
								aria-label={`Edit ${label}`}
							/>
						)}
					</div>
				</div>
			)
		}

		return (
			<div className={`form-box col-${col} ${wrapperClass}`}>
				<div className="field-lock-edit">
					<span className="field-lock-edit-label">
						{label}
						{required ? <span className="required">*</span> : ''}
					</span>
					<div className="field-lock-edit-input-row">
						<div className="field-lock-field">
							<div className={classNameString}>
								<div className="value row">
									{(type === 'checkbox' ||
										type === 'switch') &&
										descriptionJsx}
									<div
										className="inputbox col-12"
										onFocus={onFocusHandler}
										onBlur={onBlurHandler}>
										{inputPrefix
											? (() => {
													const storedSuffix = String(
														value ?? '',
													).startsWith(inputPrefix)
														? String(value).slice(
																inputPrefix.length,
															)
														: ''
													return (
														<div className="field-lock-prefix-input">
															<span className="field-lock-prefix">
																{inputPrefix}
															</span>
															<input
																id={controlName}
																type="text"
																className="form-control"
																value={
																	localSuffix ??
																	storedSuffix
																}
																onFocus={() =>
																	setLocalSuffix(
																		storedSuffix,
																	)
																}
																onChange={(e) =>
																	setLocalSuffix(
																		e.target
																			.value,
																	)
																}
																onBlur={(e) => {
																	setLocalSuffix(
																		null,
																	)
																	dispatch(
																		updateFormControlValueAction(
																			{
																				formName,
																				controlName,
																				value:
																					inputPrefix +
																					e
																						.target
																						.value,
																			},
																		),
																	)
																}}
															/>
														</div>
													)
												})()
											: children}
										{validSpan}
									</div>
									{type !== 'checkbox' &&
										type !== 'switch' &&
										descriptionJsx}
								</div>
							</div>
						</div>
						<input
							type="checkbox"
							className="field-lock-toggle"
							checked={false}
							onChange={() => setIsLocked(true)}
							aria-label={`Lock ${label}`}
						/>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className={`form-box col-${col} ${wrapperClass}`}>
			<div className={classNameString}>
				{labelJsx}

				<div className="value row">
					{(type === 'checkbox' || type === 'switch') &&
						descriptionJsx}
					<div
						className="inputbox col-12"
						onFocus={onFocusHandler}
						onBlur={onBlurHandler}>
						{children}
						{validSpan}
					</div>

					{type !== 'checkbox' && type !== 'switch' && descriptionJsx}
				</div>
			</div>
		</div>
	)
}

export default FormControlWrapper
